import { CodeBlock, Note } from "./shared";

function Endpoint({
  method,
  path,
  description,
  auth,
  body,
  response,
  curl,
}: {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  auth?: boolean;
  body?: string;
  response: string;
  curl?: string;
}) {
  const methodColors: Record<string, string> = {
    GET: "bg-green-500/10 text-green-400 ring-green-500/20",
    POST: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
    PUT: "bg-amber/10 text-amber ring-amber/20",
    DELETE: "bg-red-500/10 text-red-400 ring-red-500/20",
  };

  return (
    <div className="rounded-xl border border-elevated bg-surface/30 p-6">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span
          className={`rounded-md px-2.5 py-1 font-mono text-xs font-bold ring-1 ${methodColors[method]}`}
        >
          {method}
        </span>
        <code className="font-mono text-sm text-primary">{path}</code>
        {auth && (
          <span className="rounded-md bg-purple-500/10 px-2 py-0.5 font-mono text-xs text-purple-400 ring-1 ring-purple-500/20">
            Auth Required
          </span>
        )}
      </div>
      <p className="mb-4 text-sm text-secondary">{description}</p>
      {body && (
        <div className="mb-4">
          <p className="mb-2 font-mono text-xs font-semibold uppercase text-muted">Request Body</p>
          <CodeBlock code={body} language="json" />
        </div>
      )}
      <div className="mb-4">
        <p className="mb-2 font-mono text-xs font-semibold uppercase text-muted">Response</p>
        <CodeBlock code={response} language="json" />
      </div>
      {curl && (
        <div>
          <p className="mb-2 font-mono text-xs font-semibold uppercase text-muted">Example</p>
          <CodeBlock code={curl} language="bash" />
        </div>
      )}
    </div>
  );
}

export function ApiReferenceDocs({ apiUrl }: { apiUrl: string }) {
  return (
    <>
      {/* Overview */}
      <section id="api-overview" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">API Overview</h2>
        <p className="mt-4 text-lg text-secondary">
          All API endpoints return JSON with a consistent response format. The API is designed for
          programmatic use by agents, scripts, and integrations.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Base URL</h3>
        <div className="mt-2">
          <CodeBlock code={apiUrl} language="text" />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Response Format</h3>
        <p className="mt-2 text-secondary">Every response follows this structure:</p>
        <div className="mt-3">
          <CodeBlock
            code={`// Success
{
  "success": true,
  "data": { ... }
}

// Error
{
  "success": false,
  "error": "Human-readable error message"
}`}
            language="json"
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">HTTP Status Codes</h3>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <code className="font-mono text-sm text-amber">200</code> - Success
          </li>
          <li>
            <code className="font-mono text-sm text-amber">201</code> - Created
          </li>
          <li>
            <code className="font-mono text-sm text-amber">400</code> - Bad request (validation
            error)
          </li>
          <li>
            <code className="font-mono text-sm text-amber">401</code> - Authentication required
          </li>
          <li>
            <code className="font-mono text-sm text-amber">403</code> - Forbidden (admin only)
          </li>
          <li>
            <code className="font-mono text-sm text-amber">404</code> - Not found
          </li>
        </ul>
      </section>

      {/* Authentication */}
      <section id="api-auth" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Authentication</h2>
        <p className="mt-4 text-secondary">
          Authenticate by logging in to get a JWT token, then pass it as a Bearer token in
          subsequent requests.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Step 1: Get a Token</h3>
        <div className="mt-3">
          <Endpoint
            method="POST"
            path="/auth/login"
            description="Log in with email and password. Returns a JWT token valid for 7 days."
            body={`{
  "email": "user@example.com",
  "password": "your-password"
}`}
            response={`{
  "success": true,
  "data": {
    "user": { "id": "clx...", "email": "user@example.com", "name": "User" },
    "token": "eyJhbGciOiJIUzI1NiIs..."
  }
}`}
            curl={`curl -X POST ${apiUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "your-password"}'`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Step 2: Use the Token</h3>
        <p className="mt-2 text-secondary">
          Include the token in the Authorization header for all authenticated requests:
        </p>
        <div className="mt-3">
          <CodeBlock code={`Authorization: Bearer eyJhbGciOiJIUzI1NiIs...`} language="text" />
        </div>

        <div className="mt-6 space-y-4">
          <Endpoint
            method="POST"
            path="/auth/signup"
            description="Create a new account. Returns user info and JWT token."
            body={`{
  "email": "newuser@example.com",
  "password": "secure-password",
  "name": "New User"
}`}
            response={`{
  "success": true,
  "data": {
    "user": { "id": "clx...", "email": "newuser@example.com", "name": "New User" },
    "token": "eyJhbGciOiJIUzI1NiIs..."
  }
}`}
          />

          <Endpoint
            method="GET"
            path="/auth/me"
            description="Get the currently authenticated user."
            auth
            response={`{
  "success": true,
  "data": {
    "user": { "userId": "clx...", "email": "user@example.com", "isAdmin": false }
  }
}`}
          />
        </div>
      </section>

      {/* Marketplaces */}
      <section id="api-marketplaces" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Custom Marketplaces</h2>
        <p className="mt-4 text-secondary">
          Create curated collections of plugins. Each marketplace gets a shareable link at{" "}
          <code className="font-mono text-sm text-amber">/m/:slug</code>.
        </p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/marketplaces"
            description="List all marketplaces owned by the authenticated user."
            auth
            response={`{
  "success": true,
  "data": [
    {
      "id": "clx...",
      "name": "My Dev Tools",
      "slug": "my-dev-tools",
      "description": "Curated developer plugins",
      "ownerId": "clx...",
      "createdAt": "2026-02-18T...",
      "updatedAt": "2026-02-18T...",
      "_count": { "plugins": 3 }
    }
  ]
}`}
            curl={`curl ${apiUrl}/marketplaces \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />

          <Endpoint
            method="POST"
            path="/marketplaces"
            description="Create a new marketplace. A URL slug is auto-generated from the name."
            auth
            body={`{
  "name": "My Dev Tools",
  "description": "Optional description"
}`}
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "name": "My Dev Tools",
    "slug": "my-dev-tools",
    "description": "Optional description",
    "ownerId": "clx...",
    "_count": { "plugins": 0 }
  }
}`}
            curl={`curl -X POST ${apiUrl}/marketplaces \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My Dev Tools", "description": "Curated developer plugins"}'`}
          />

          <Endpoint
            method="GET"
            path="/marketplaces/:id"
            description="Get a specific marketplace with its plugin IDs (owner only)."
            auth
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "name": "My Dev Tools",
    "slug": "my-dev-tools",
    "description": "...",
    "plugins": [
      { "pluginId": "clx...", "order": 0 },
      { "pluginId": "clx...", "order": 1 }
    ]
  }
}`}
          />

          <Endpoint
            method="PUT"
            path="/marketplaces/:id"
            description="Update marketplace name and/or description. Slug is regenerated from name."
            auth
            body={`{
  "name": "Updated Name",
  "description": "Updated description"
}`}
            response={`{
  "success": true,
  "data": { "id": "clx...", "name": "Updated Name", "slug": "updated-name", ... }
}`}
          />

          <Endpoint
            method="DELETE"
            path="/marketplaces/:id"
            description="Delete a marketplace and all its plugin selections. Owner only."
            auth
            response={`{
  "success": true,
  "data": { "deleted": true }
}`}
            curl={`curl -X DELETE ${apiUrl}/marketplaces/MARKETPLACE_ID \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />
        </div>
      </section>

      {/* Marketplace Plugins */}
      <section id="api-marketplace-plugins" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Marketplace Plugins</h2>
        <p className="mt-4 text-secondary">
          Add and remove plugins from a marketplace. Any published plugin can be added.
        </p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="POST"
            path="/marketplaces/:id/plugins"
            description="Add a published plugin to the marketplace."
            auth
            body={`{
  "pluginId": "clx...",
  "order": 0
}`}
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "marketplaceId": "clx...",
    "pluginId": "clx...",
    "order": 0,
    "addedAt": "2026-02-18T..."
  }
}`}
            curl={`curl -X POST ${apiUrl}/marketplaces/MARKETPLACE_ID/plugins \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"pluginId": "PLUGIN_ID", "order": 0}'`}
          />

          <Endpoint
            method="DELETE"
            path="/marketplaces/:id/plugins/:pluginId"
            description="Remove a plugin from the marketplace."
            auth
            response={`{
  "success": true,
  "data": { "removed": true }
}`}
          />

          <Endpoint
            method="PUT"
            path="/marketplaces/:id/plugins/reorder"
            description="Reorder plugins within the marketplace."
            auth
            body={`{
  "plugins": [
    { "pluginId": "clx...", "order": 0 },
    { "pluginId": "clx...", "order": 1 },
    { "pluginId": "clx...", "order": 2 }
  ]
}`}
            response={`{
  "success": true,
  "data": { "reordered": true }
}`}
          />
        </div>
      </section>

      {/* Public View */}
      <section id="api-public" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Public Endpoints</h2>
        <p className="mt-4 text-secondary">These endpoints don't require authentication.</p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/api/marketplaces/:slug"
            description="Get a marketplace's public view with full plugin data. No auth required."
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "name": "My Dev Tools",
    "slug": "my-dev-tools",
    "description": "Curated developer plugins",
    "owner": { "id": "clx...", "name": "User", "avatarUrl": null },
    "plugins": [
      {
        "id": "clx...",
        "name": "code-simplifier",
        "slug": "code-simplifier",
        "description": "Simplify complex code",
        "author": { "id": "clx...", "name": "Author" },
        "files": [...],
        "_count": { "downloads": 42, "reviews": 3, "favorites": 7 },
        "order": 0,
        "addedAt": "2026-02-18T..."
      }
    ]
  }
}`}
            curl={`curl ${apiUrl}/api/marketplaces/my-dev-tools`}
          />

          <Endpoint
            method="GET"
            path="/api/plugins"
            description="List all published plugins. Useful for discovering plugin IDs to add to marketplaces."
            response={`{
  "success": true,
  "data": [
    {
      "id": "clx...",
      "name": "code-simplifier",
      "slug": "code-simplifier",
      "description": "...",
      "tags": ["developer", "refactoring"],
      ...
    }
  ]
}`}
            curl={`curl ${apiUrl}/api/plugins`}
          />
        </div>
      </section>

      {/* Agent Workflow */}
      <section id="api-agent-workflow" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Agent Workflow</h2>
        <p className="mt-4 text-secondary">
          Complete example: authenticate, create a marketplace, add plugins, and get the shareable
          link.
        </p>

        <div className="mt-6">
          <Note>
            All commands use <code className="font-mono text-xs">curl</code>. Replace{" "}
            <code className="font-mono text-xs">YOUR_TOKEN</code> with the JWT from login.
          </Note>
        </div>

        <div className="mt-6">
          <CodeBlock
            code={`# 1. Authenticate
TOKEN=$(curl -s -X POST ${apiUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "agent@example.com", "password": "password"}' \\
  | jq -r '.data.token')

# 2. List available plugins
curl -s ${apiUrl}/api/plugins | jq '.data[] | {id, name, slug}'

# 3. Create a marketplace
MARKETPLACE=$(curl -s -X POST ${apiUrl}/marketplaces \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Agent Picks", "description": "AI-curated plugins"}')
MARKETPLACE_ID=$(echo $MARKETPLACE | jq -r '.data.id')
MARKETPLACE_SLUG=$(echo $MARKETPLACE | jq -r '.data.slug')

# 4. Add plugins to the marketplace
curl -s -X POST ${apiUrl}/marketplaces/$MARKETPLACE_ID/plugins \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"pluginId": "PLUGIN_ID_1", "order": 0}'

curl -s -X POST ${apiUrl}/marketplaces/$MARKETPLACE_ID/plugins \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"pluginId": "PLUGIN_ID_2", "order": 1}'

# 5. View the marketplace (public, no auth needed)
curl -s ${apiUrl}/api/marketplaces/$MARKETPLACE_SLUG | jq

# Shareable URL: ${apiUrl.replace(/\/api.*/, "")}/m/$MARKETPLACE_SLUG`}
            language="bash"
          />
        </div>
      </section>
    </>
  );
}
